import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { Camera, FileText, Image as ImageIcon, Sparkles, Upload, X, AlertCircle } from 'lucide-react';
import { useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  type ButtonProps,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  useToast,
} from '@/components/ui';
import { ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  LEARNING_ACCEPT,
  LEARNING_MAX_FILES,
  LEARNING_MAX_TOTAL_BYTES,
  fileProblem,
  learningApi,
  learningKeys,
  type FileProblem,
} from './learning-api';

interface Picked {
  file: File;
  problem: FileProblem | null;
}

/**
 * "ללמוד ממסמכים" — דיאלוג העלאה לשימוש חוזר: דשבורד, מחירון, לקוחות, אשף ההצטרפות.
 *
 * גרירה, בחירה מרובה, וצילום ישיר מהטלפון. בסיום: `onStarted` (האשף מציג את
 * ההתקדמות שלו), ובלעדיו — מעבר ל-/learn שמציג התקדמות ואז את מסך האישור.
 */
export function LearnFromDocumentsDialog({
  onStarted,
  trigger,
  triggerVariant = 'secondary',
  triggerSize = 'sm',
}: {
  onStarted?: (runId: string) => void;
  /** כפתור משלך. בלי — "ללמוד ממסמכים". */
  trigger?: ReactNode;
  triggerVariant?: ButtonProps['variant'];
  triggerSize?: ButtonProps['size'];
}) {
  const { t } = useTranslation('learning');
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  return (
    <Dialog open={open} onOpenChange={(v) => (busy && !v ? undefined : setOpen(v))}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant={triggerVariant} size={triggerSize}>
            <Sparkles aria-hidden />
            {t('dialog.button')}
          </Button>
        )}
      </DialogTrigger>

      <DialogContent>
        {/* התוכן נטען רק כשהדיאלוג פתוח — כך הכפתור לא דורש Toast/Router בדפים ובבדיקות. */}
        <LearnDialogBody
          onStarted={onStarted}
          onClose={() => setOpen(false)}
          onBusyChange={setBusy}
        />
      </DialogContent>
    </Dialog>
  );
}

function LearnDialogBody({
  onStarted,
  onClose,
  onBusyChange,
}: {
  onStarted?: (runId: string) => void;
  onClose: () => void;
  onBusyChange: (busy: boolean) => void;
}) {
  const { t } = useTranslation(['learning', 'common']);
  const toast = useToast();
  const qc = useQueryClient();
  // בלי אזהרה: הדיאלוג מרונדר גם בבדיקות דפים בלי RouterProvider.
  const router = useRouter({ warn: false });
  const [picked, setPicked] = useState<Picked[]>([]);
  const [dragging, setDragging] = useState(false);
  const filesRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  // בלי LLM בשרת אין קריאה של PDF וצילומים — אומרים לפני ההעלאה, לא אחריה.
  const status = useQuery({
    queryKey: [...learningKeys.all, 'status'],
    queryFn: ({ signal }) => learningApi.status(signal),
    staleTime: 5 * 60_000,
  });
  const aiAvailable = status.data?.aiAvailable ?? true;

  const add = (list: FileList | File[] | null) => {
    if (!list) return;
    const next = [...list].map((file) => ({ file, problem: fileProblem(file, aiAvailable) }));
    setPicked((prev) => {
      const seen = new Set(prev.map((p) => `${p.file.name}:${p.file.size}`));
      return [...prev, ...next.filter((p) => !seen.has(`${p.file.name}:${p.file.size}`))];
    });
  };

  const valid = picked.filter((p) => !p.problem);
  const tooMany = valid.length > LEARNING_MAX_FILES;
  const tooHeavy = valid.reduce((n, p) => n + p.file.size, 0) > LEARNING_MAX_TOTAL_BYTES;

  const upload = useMutation({
    mutationFn: () => learningApi.upload(valid.map((p) => p.file)),
    onSuccess: ({ runId }) => {
      toast.success(t('dialog.started'), t('dialog.startedHint'));
      void qc.invalidateQueries({ queryKey: learningKeys.all });
      onClose();
      if (onStarted) onStarted(runId);
      else void router?.navigate({ to: '/learn', search: { run: runId } });
    },
    onError: (err: unknown) =>
      toast.error(t('dialog.failed'), err instanceof ApiError ? err.message : undefined),
  });

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragging(false);
    add(e.dataTransfer.files);
  };

  useEffect(() => onBusyChange(upload.isPending), [upload.isPending, onBusyChange]);

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('dialog.title')}</DialogTitle>
        <DialogDescription>{t('dialog.description')}</DialogDescription>
      </DialogHeader>

      <DialogBody className="space-y-3">
        {!aiAvailable ? (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-(--radius-md) border border-warning-border bg-warning-subtle p-3 text-xs text-fg"
          >
            <AlertCircle className="mt-px size-4 shrink-0 text-warning" aria-hidden />
            <span>{t('dialog.noAi')}</span>
          </div>
        ) : null}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          data-testid="learn-dropzone"
          className={cn(
            'flex flex-col items-center gap-3 rounded-(--radius-md) border border-dashed p-5 text-center transition-colors',
            dragging ? 'border-accent bg-accent-subtle' : 'border-border bg-surface',
          )}
        >
          <Upload className="size-6 text-fg-subtle" aria-hidden />
          <p className="text-sm text-fg-muted">{t('dialog.drop')}</p>
          <div className="flex flex-wrap justify-center gap-2">
            <Button variant="secondary" size="sm" onClick={() => filesRef.current?.click()}>
              <FileText aria-hidden />
              {t('dialog.choose')}
            </Button>
            {aiAvailable ? (
              <Button variant="secondary" size="sm" onClick={() => cameraRef.current?.click()}>
                <Camera aria-hidden />
                {t('dialog.camera')}
              </Button>
            ) : null}
          </div>
          <p className="text-2xs text-fg-subtle">{t('dialog.hint')}</p>
          <input
            ref={filesRef}
            type="file"
            multiple
            accept={aiAvailable ? LEARNING_ACCEPT : '.xlsx,.csv'}
            className="sr-only"
            aria-label={t('dialog.choose')}
            onChange={(e) => {
              add(e.target.files);
              e.target.value = '';
            }}
          />
          {/* capture: בטלפון נפתחת המצלמה ישירות (ו-iOS שומר JPEG ולא HEIC). */}
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            aria-label={t('dialog.camera')}
            onChange={(e) => {
              add(e.target.files);
              e.target.value = '';
            }}
          />
        </div>

        {picked.length > 0 ? (
          <ul className="space-y-1.5" aria-label={t('dialog.filesAria')}>
            {picked.map((p) => (
              <li
                key={`${p.file.name}:${p.file.size}`}
                className="flex items-center justify-between gap-2 rounded-(--radius-md) border border-border bg-surface px-3 py-2 text-sm"
              >
                <span className="flex min-w-0 items-center gap-2">
                  {p.file.type.startsWith('image/') ? (
                    <ImageIcon className="size-4 shrink-0 text-fg-subtle" aria-hidden />
                  ) : (
                    <FileText className="size-4 shrink-0 text-fg-subtle" aria-hidden />
                  )}
                  <span className="min-w-0">
                    <span dir="auto" className="block truncate">
                      {p.file.name}
                    </span>
                    {p.problem ? (
                      <span className="block text-2xs text-danger">
                        {t(`dialog.problems.${p.problem}`)}
                      </span>
                    ) : null}
                  </span>
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('dialog.remove', { name: p.file.name })}
                  disabled={upload.isPending}
                  onClick={() => setPicked((prev) => prev.filter((x) => x !== p))}
                >
                  <X aria-hidden />
                </Button>
              </li>
            ))}
          </ul>
        ) : null}
        {tooMany ? <p className="text-xs text-danger">{t('dialog.tooMany')}</p> : null}
        {tooHeavy ? <p className="text-xs text-danger">{t('dialog.tooHeavy')}</p> : null}
        <p className="text-2xs text-fg-subtle">{t('dialog.privacy')}</p>
      </DialogBody>

      <DialogFooter>
        <Button variant="ghost" size="sm" disabled={upload.isPending} onClick={onClose}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          size="sm"
          loading={upload.isPending}
          disabled={valid.length === 0 || tooMany || tooHeavy}
          onClick={() => upload.mutate()}
        >
          {upload.isPending ? t('dialog.uploading') : t('dialog.submit', { count: valid.length })}
        </Button>
      </DialogFooter>
    </>
  );
}
